import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";

export default function MultiLoginBoard() {
    const { http, user } = AuthUser();
    const [groups, setGroups] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editBoard, setEditBoard] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});

    // Add form state
    const [boardName, setBoardName] = useState("");
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [primaryCompanyId, setPrimaryCompanyId] = useState(null);
    const [companySearch, setCompanySearch] = useState("");

    // Edit form state
    const [editFormData, setEditFormData] = useState({ company_id: "", name: "" });

    useEffect(() => {
        fetchBoards();
        fetchCompanies();
    }, []);

    const fetchBoards = () => {
        setIsLoading(true);
        http.get("/multi_login_boards")
            .then((res) => {
                setIsLoading(false);
                if (Array.isArray(res.data.data)) {
                    setGroups(res.data.data);
                    const expanded = {};
                    res.data.data.forEach((g) => { expanded[g.name] = true; });
                    setExpandedGroups(expanded);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching boards:", error);
            });
    };

    const fetchCompanies = () => {
        setCompaniesLoading(true);
        http.get("/company")
            .then((res) => {
                setCompaniesLoading(false);
                if (res.data && Array.isArray(res.data.data)) {
                    setCompanies(res.data.data);
                } else if (Array.isArray(res.data)) {
                    setCompanies(res.data);
                } else {
                    setCompanies([]);
                }
            })
            .catch((error) => {
                setCompaniesLoading(false);
                console.error("Error fetching companies:", error);
                toast.error("Failed to load companies");
            });
    };

    const toggleGroup = (name) => {
        setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    // --- Add Modal ---
    const filteredCompanies = companies.filter((c) =>
        (c.company_name || "").toLowerCase().includes(companySearch.toLowerCase()) ||
        (c.company_id || "").toLowerCase().includes(companySearch.toLowerCase())
    );

    const toggleCompanySelect = (companyId) => {
        setSelectedCompanies((prev) => {
            const next = prev.includes(companyId)
                ? prev.filter((id) => id !== companyId)
                : [...prev, companyId];
            if (!next.includes(primaryCompanyId)) {
                setPrimaryCompanyId(next.length > 0 ? next[0] : null);
            }
            return next;
        });
    };

    const selectAllCompanies = () => {
        if (selectedCompanies.length === filteredCompanies.length) {
            setSelectedCompanies([]);
            setPrimaryCompanyId(null);
        } else {
            const all = filteredCompanies.map((c) => c.id);
            setSelectedCompanies(all);
            if (!all.includes(primaryCompanyId)) {
                setPrimaryCompanyId(all[0] || null);
            }
        }
    };

    const openAddModal = () => {
        setSelectedCompanies([]);
        setPrimaryCompanyId(null);
        setBoardName("");
        setCompanySearch("");
        setShowAddModal(true);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (selectedCompanies.length === 0) {
            toast.error("Please select at least one company");
            return;
        }
        if (!boardName.trim()) {
            toast.error("Please enter a board name");
            return;
        }
        if (!primaryCompanyId) {
            toast.error("Please select a primary company");
            return;
        }

        try {
            let successCount = 0;
            let duplicateCount = 0;

            for (const companyId of selectedCompanies) {
                try {
                    await http.post("/multi_login_board_add", {
                        company_id: companyId,
                        name: boardName.trim(),
                        is_primary: companyId === primaryCompanyId ? 1 : 0,
                    });
                    successCount++;
                } catch (error) {
                    if (error.response && error.response.status === 409) {
                        duplicateCount++;
                    } else {
                        throw error;
                    }
                }
            }

            if (successCount > 0) toast.success(`${successCount} board(s) added successfully!`);
            if (duplicateCount > 0) toast.error(`${duplicateCount} already existed (skipped)`);

            setShowAddModal(false);
            fetchBoards();
        } catch (error) {
            toast.error("Error adding board");
            console.error("Error adding board:", error);
        }
    };

    // --- Set Primary ---
    const handleSetPrimary = async (id) => {
        try {
            await http.put(`/multi_login_board_set_primary/${id}`);
            toast.success("Primary company updated!");
            fetchBoards();
        } catch (error) {
            toast.error("Error setting primary");
            console.error(error);
        }
    };

    // --- Edit ---
    const handleEditOpen = (item) => {
        setEditBoard(item);
        setEditFormData({
            company_id: item.company_id,
            name: item.name || "",
            current_company_name: item.company_name || "",
            current_company_code: item.company_code || "",
        });
        setShowEditModal(true);
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        try {
            await http.put(`/multi_login_board_edit/${editBoard.id}`, editFormData);
            toast.success("Updated successfully!");
            setShowEditModal(false);
            setEditBoard(null);
            fetchBoards();
        } catch (error) {
            toast.error("Error updating");
            console.error(error);
        }
    };

    // --- Delete ---
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this entry?")) return;
        try {
            await http.delete(`/multi_login_board_delete/${id}`);
            toast.success("Deleted successfully!");
            fetchBoards();
        } catch (error) {
            toast.error("Error deleting");
            console.error(error);
        }
    };

    const getCompanyById = (id) => companies.find((c) => c.id === id);

    return (
        <div className="container-fluid pt-4 px-4">
            <Toaster />
            <div className="row g-4">
                <div className="col-12">
                    <div className="bg-light rounded h-100 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="mb-0">Multi-Login-Board</h6>
                            <button className="btn btn-primary btn-sm" onClick={openAddModal}>
                                <i className="fa fa-plus me-2"></i>Add Board
                            </button>
                        </div>

                        {isLoading ? (
                            <TaskSkeleton />
                        ) : groups.length === 0 ? (
                            <div className="text-center text-muted p-4">No boards found</div>
                        ) : (
                            groups.map((group) => (
                                <div key={group.name} className="card mb-3 shadow-sm">
                                    {/* Group Header */}
                                    <div
                                        className="card-header d-flex justify-content-between align-items-center"
                                        style={{ cursor: "pointer", backgroundColor: "#0d6efd", color: "#fff" }}
                                        onClick={() => toggleGroup(group.name)}
                                    >
                                        <div className="d-flex align-items-center">
                                            <i className={`fa fa-chevron-${expandedGroups[group.name] ? "down" : "right"} me-2`}></i>
                                            <strong>{group.name}</strong>
                                            <span className="badge bg-light text-dark ms-2">
                                                {(group.primary ? 1 : 0) + group.children.length} companies
                                            </span>
                                        </div>
                                    </div>

                                    {/* Group Body */}
                                    {expandedGroups[group.name] && (
                                        <div className="card-body p-0">
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: "40px" }}></th>
                                                        <th>Company</th>
                                                        <th>Code</th>
                                                        <th>Role</th>
                                                        <th>Created</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {/* Primary Row */}
                                                    {group.primary && (
                                                        <tr style={{ backgroundColor: "#e8f5e9" }}>
                                                            <td><i className="fa fa-star text-warning"></i></td>
                                                            <td><strong>{group.primary.company_name}</strong></td>
                                                            <td>{group.primary.company_code}</td>
                                                            <td>
                                                                <span className="badge bg-success">Primary</span>
                                                            </td>
                                                            <td>{moment(group.primary.created_at).format("DD-MM-YYYY")}</td>
                                                            <td>
                                                                <button className="btn btn-sm btn-warning me-1" onClick={() => handleEditOpen({ ...group.primary, name: group.name })}>
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(group.primary.id)}>
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )}

                                                    {/* Child Rows */}
                                                    {group.children.map((child) => (
                                                        <tr key={child.id}>
                                                            <td><i className="fa fa-long-arrow-alt-right text-muted ms-2"></i></td>
                                                            <td>{child.company_name}</td>
                                                            <td>{child.company_code}</td>
                                                            <td>
                                                                <span className="badge bg-secondary">Child</span>
                                                            </td>
                                                            <td>{moment(child.created_at).format("DD-MM-YYYY")}</td>
                                                            <td>
                                                                <button className="btn btn-sm btn-outline-success me-1" title="Set as Primary"
                                                                    onClick={() => handleSetPrimary(child.id)}>
                                                                    <i className="fa fa-star"></i>
                                                                </button>
                                                                <button className="btn btn-sm btn-warning me-1" onClick={() => handleEditOpen({ ...child, name: group.name })}>
                                                                    <i className="fa fa-edit"></i>
                                                                </button>
                                                                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(child.id)}>
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}

                                                    {!group.primary && group.children.length === 0 && (
                                                        <tr>
                                                            <td colSpan="6" className="text-center text-muted">No companies in this board</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Multi-Login Board</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
                            </div>
                            <form onSubmit={handleAdd}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Board Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={boardName}
                                            onChange={(e) => setBoardName(e.target.value)}
                                            placeholder="Enter board name"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">
                                            Select Companies
                                            {selectedCompanies.length > 0 && (
                                                <span className="badge bg-primary ms-2">{selectedCompanies.length} selected</span>
                                            )}
                                        </label>

                                        {/* Selected companies as tags */}
                                        {selectedCompanies.length > 0 && (
                                            <div className="mb-2 d-flex flex-wrap gap-1">
                                                {selectedCompanies.map((id) => {
                                                    const c = getCompanyById(id);
                                                    const isPrimary = id === primaryCompanyId;
                                                    return c ? (
                                                        <span key={id}
                                                            className={`badge d-flex align-items-center ${isPrimary ? "bg-success" : "bg-info text-dark"}`}
                                                            style={{ fontSize: "12px" }}>
                                                            {isPrimary && <i className="fa fa-star me-1"></i>}
                                                            {c.company_name}
                                                            <i className="fa fa-times ms-1" style={{ cursor: "pointer" }}
                                                                onClick={() => toggleCompanySelect(id)}></i>
                                                        </span>
                                                    ) : null;
                                                })}
                                            </div>
                                        )}

                                        {/* Search */}
                                        <input
                                            type="text"
                                            className="form-control mb-2"
                                            placeholder="Search companies..."
                                            value={companySearch}
                                            onChange={(e) => setCompanySearch(e.target.value)}
                                        />

                                        {companiesLoading ? (
                                            <div className="text-center p-3">
                                                <span className="spinner-border spinner-border-sm me-2"></span>Loading companies...
                                            </div>
                                        ) : (
                                            <div className="border rounded" style={{ maxHeight: "250px", overflowY: "auto" }}>
                                                {filteredCompanies.length > 0 && (
                                                    <div className="px-3 py-2 border-bottom d-flex align-items-center"
                                                        style={{ backgroundColor: "#f0f0f0", cursor: "pointer" }}
                                                        onClick={selectAllCompanies}>
                                                        <input className="form-check-input me-2" type="checkbox"
                                                            checked={selectedCompanies.length === filteredCompanies.length && filteredCompanies.length > 0}
                                                            onChange={selectAllCompanies} />
                                                        <strong>Select All ({filteredCompanies.length})</strong>
                                                    </div>
                                                )}

                                                {filteredCompanies.length > 0 ? (
                                                    filteredCompanies.map((company) => {
                                                        const isSelected = selectedCompanies.includes(company.id);
                                                        const isPrimary = company.id === primaryCompanyId;
                                                        return (
                                                            <div key={company.id}
                                                                className={`px-3 py-2 border-bottom d-flex align-items-center justify-content-between ${isSelected ? "bg-light" : ""}`}>
                                                                <div className="d-flex align-items-center" style={{ cursor: "pointer" }}
                                                                    onClick={() => toggleCompanySelect(company.id)}>
                                                                    <input className="form-check-input me-2" type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => toggleCompanySelect(company.id)} />
                                                                    <span>{company.company_name} <small className="text-muted">({company.company_id})</small></span>
                                                                </div>
                                                                {isSelected && (
                                                                    <button
                                                                        type="button"
                                                                        className={`btn btn-sm ${isPrimary ? "btn-success" : "btn-outline-secondary"}`}
                                                                        onClick={() => setPrimaryCompanyId(company.id)}
                                                                        title="Set as Primary"
                                                                    >
                                                                        <i className="fa fa-star me-1"></i>
                                                                        {isPrimary ? "Primary" : "Set Primary"}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="text-center p-3 text-muted">
                                                        {companies.length === 0 ? "No companies available" : "No matching companies"}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary"
                                        disabled={selectedCompanies.length === 0 || !boardName.trim() || !primaryCompanyId}>
                                        Save ({selectedCompanies.length} companies)
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowEditModal(false); }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Board Entry</h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <form onSubmit={handleEditSave}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Company</label>
                                        <div className="input-group">
                                            <span className="input-group-text"><i className="fa fa-building"></i></span>
                                            <select className="form-select" name="company_id"
                                                value={editFormData.company_id}
                                                onChange={(e) => setEditFormData({ ...editFormData, company_id: e.target.value })} required>
                                                {companies.length > 0 ? (
                                                    <>
                                                        <option value="">-- Select Company --</option>
                                                        {companies.map((c) => (
                                                            <option key={c.id} value={c.id}>{c.company_name} ({c.company_id})</option>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <option value={editFormData.company_id}>
                                                        {editFormData.current_company_name} ({editFormData.current_company_code})
                                                    </option>
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Board Name</label>
                                        <input type="text" className="form-control" value={editFormData.name}
                                            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                            placeholder="Enter board name" required />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary">Update</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
